import React from 'react';
import { ICONS_MAP } from '../../utils/constants';
import { useLanguage } from '../../contexts/LanguageContext';

// Icon component
const Icon = ({ path, className = 'w-6 h-6' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
        <path d={path} />
    </svg>
);

const safeCoerceNumber = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    const num = Number(String(val).replace(/[^0-9.-]+/g, ''));
    return isNaN(num) ? 0 : num;
};

const MobileOrderCard = ({ order, customer, onClick, handleOrderAction, statusConfig, settings, generalSettings, formatCurrency, shippingFees, onCustomerClick, shippingMap }) => {
    const { t } = useLanguage();
    const total = parseFloat(order.total) || 0;

    // Use passed shippingMap instead of calculating it every time
    const shippingFee = customer ? (safeCoerceNumber(shippingMap?.get(`${customer.state?.trim()}-${customer.city?.trim()}`)?.fees) || 0) : 0;

    // Tailwind Safelist mapping
    const STATUS_THEMES = {
        blue: { bg100: 'bg-blue-100', text800: 'text-blue-800', bg600: 'bg-blue-600', hover700: 'hover:bg-blue-700', text600: 'text-blue-600' },
        purple: { bg100: 'bg-purple-100', text800: 'text-purple-800', bg600: 'bg-purple-600', hover700: 'hover:bg-purple-700', text600: 'text-purple-600' },
        yellow: { bg100: 'bg-yellow-100', text800: 'text-yellow-800', bg600: 'bg-yellow-600', hover700: 'hover:bg-yellow-700', text600: 'text-yellow-600' },
        green: { bg100: 'bg-green-100', text800: 'text-green-800', bg600: 'bg-green-600', hover700: 'hover:bg-green-700', text600: 'text-green-600' },
        red: { bg100: 'bg-red-100', text800: 'text-red-800', bg600: 'bg-red-600', hover700: 'hover:bg-red-700', text600: 'text-red-600' },
        orange: { bg100: 'bg-orange-100', text800: 'text-orange-800', bg600: 'bg-orange-600', hover700: 'hover:bg-orange-700', text600: 'text-orange-600' },
        gray: { bg100: 'bg-gray-100', text800: 'text-gray-800', bg600: 'bg-gray-600', hover700: 'hover:bg-gray-700', text600: 'text-gray-600' },
        indigo: { bg100: 'bg-indigo-100', text800: 'text-indigo-800', bg600: 'bg-indigo-600', hover700: 'hover:bg-indigo-700', text600: 'text-indigo-600' }
    };

    const getTheme = (color) => STATUS_THEMES[color] || STATUS_THEMES.indigo;

    // Format products for condensed display
    const productsText = (order.items || []).map(item => `${item.pid} – ${item.qty}`).join('\n');
    const productsLines = productsText.split('\n');
    const displayProducts = productsLines.slice(0, 3).join('\n');
    const moreCount = productsLines.length > 3 ? productsLines.length - 3 : 0;

    // Determine possible actions (same as desktop)
    const getPossibleActions = (currentStatus) => {
        switch (currentStatus) {
            case 'ordered':
                return [
                    { action: 'advance', label: t('ship'), color: statusConfig.shipped.color, icon: 'shipped' },
                    { action: 'cancel', label: t('cancel'), color: statusConfig.cancelled.color, icon: 'cancelled' }
                ];
            case 'shipped':
                return [
                    { action: 'back', label: t('backToOrdered'), color: statusConfig.ordered.color, icon: 'ordered' },
                    { action: 'advance', label: t('deliver'), color: statusConfig.delivered.color, icon: 'delivered' },
                    { action: 'cancel', label: t('cancel'), color: statusConfig.cancelled.color, icon: 'cancelled' }
                ];
            case 'delivered':
                return [
                    { action: 'back', label: t('backToShipped'), color: statusConfig.shipped.color, icon: 'shipped' },
                    { action: 'complete', label: t('complete'), color: statusConfig.completed.color, icon: 'completed' }
                ];
            case 'completed':
                return [
                    { action: 'return', label: t('return'), color: statusConfig.returned.color, icon: 'returned' },
                    { action: 'cancel', label: t('cancel'), color: statusConfig.cancelled.color, icon: 'cancelled' }
                ];
            case 'cancelled':
                return [
                    { action: 'reopen', label: t('reOrder'), color: statusConfig.ordered.color, icon: 'ordered' },
                    { action: 'return', label: t('markReturned'), color: statusConfig.returned.color, icon: 'returned' },
                ];
            case 'returned':
                return [
                    { action: 'reopen', label: t('reOrder'), color: statusConfig.ordered.color, icon: 'ordered' },
                    { action: 'cancel', label: t('markCancelled'), color: statusConfig.cancelled.color, icon: 'cancelled' },
                ];
            case 'deleted':
                return [
                    { action: 'reopen', label: t('restoreToCancelled'), color: statusConfig.cancelled.color, icon: 'cancelled' },
                    { action: 'perm_delete', label: t('permanentlyDelete'), color: statusConfig.deleted.color, icon: 'trash' }
                ];
            default:
                return [];
        }
    };

    const possibleActions = getPossibleActions(order.status);
    const orderStatusColor = statusConfig[order.status]?.color || 'gray';

    return (
        <div
            className="bg-white rounded-lg shadow-md p-4 mb-4 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={onClick}
        >
            {/* Header: Order ID, Payment and Status */}
            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900 bg-gray-100 px-2.5 py-1 rounded-lg text-sm shadow-sm border border-gray-200">{String(order.orderid).replace(/^#/, '')}</span>
                    {order.woo_order_id && (
                        <span className="font-bold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg text-xs shadow-sm border border-indigo-100 flex items-center gap-1">
                            {order.woo_order_id}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${order.payment === 'Visa' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                        {order.payment === 'Visa' ? t('visaPayment') : t('cashPayment')}
                    </span>
                    <span className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getTheme(orderStatusColor).bg100} ${getTheme(orderStatusColor).text800}`}>
                        <Icon path={ICONS_MAP[statusConfig[order.status]?.icon || 'ordered']} className="w-3 h-3" />
                        {t(order.status)}
                    </span>
                </div>
            </div>

            {/* 2-Column Layout */}
            <div className="flex justify-between">
                {/* Left Column */}
                <div className="flex-1">
                    {/* Customer Name and Phone */}
                    <div className="mb-2">
                        <p
                            className="font-semibold text-indigo-600 hover:text-indigo-800 cursor-pointer hover:underline"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (customer && onCustomerClick) {
                                    onCustomerClick(customer);
                                }
                            }}
                        >
                            {customer?.name || 'N/A'}
                        </p>
                        {customer?.mobile && (
                            <a href={`tel:${customer.mobile}`} className="text-blue-600 text-sm hover:underline">
                                {customer.mobile}
                            </a>
                        )}
                    </div>

                    {/* Location */}
                    <div className="mb-2 text-sm text-gray-600">
                        <p className="line-clamp-2">{customer?.state || 'N/A'} - {customer?.city || ''}</p>
                    </div>

                    {/* Products */}
                    <div className="mb-2">
                        <p className="text-sm text-gray-700 line-clamp-3 whitespace-pre-wrap">
                            {displayProducts}
                            {moreCount > 0 && `\n+${moreCount} ${t('more') || 'more'}`}
                        </p>
                    </div>
                </div>

                {/* Right Column */}
                <div className="text-right">
                    {/* Total and Shipping */}
                    <div className="mb-4">
                        <span className="font-bold text-indigo-600">{formatCurrency(total, generalSettings?.currency || 'GBP')}</span>
                        <div className="text-xs text-gray-500">{t('shipping')}: {formatCurrency(shippingFee, generalSettings?.currency || 'GBP')}</div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-1 justify-end">
                        {possibleActions.map(act => {
                            const isButton = ['complete', 'reopen', 'perm_delete'].includes(act.action);
                            const actTheme = getTheme(act.color);
                            if (isButton) {
                                return (
                                    <button
                                        key={act.action}
                                        onClick={(e) => { e.stopPropagation(); handleOrderAction(order.orderid, act.action); }}
                                        className={`px-3 py-1 text-xs font-medium rounded ${actTheme.bg600} text-white ${actTheme.hover700} transition-colors`}
                                    >
                                        {act.label}
                                    </button>
                                );
                            } else {
                                return (
                                    <button
                                        key={act.action}
                                        onClick={(e) => { e.stopPropagation(); handleOrderAction(order.orderid, act.action); }}
                                        className={`p-2 rounded-full hover:bg-gray-200 transition-colors`}
                                        title={act.label}
                                    >
                                        <Icon path={ICONS_MAP[act.icon]} className={`w-4 h-4 ${actTheme.text600}`} />
                                    </button>
                                );
                            }
                        })}
                        {/* Soft Delete only available if not already deleted */}
                        {order.status !== 'deleted' && (
                            <button
                                onClick={(e) => { e.stopPropagation(); handleOrderAction(order.orderid, 'delete'); }}
                                className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                                title={t('softDelete')}
                            >
                                <Icon path={ICONS_MAP['trash']} className="w-4 h-4 text-gray-600" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(MobileOrderCard);